import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ConnectedUser } from './shared/connected-user';


@Injectable({
   providedIn: 'root'
})
export class UserSessionService {

   private readonly STORAGE_KEY = 'connectedUser';
   private cachedUser: ConnectedUser | null = null;
   private readonly userSubject = new BehaviorSubject<ConnectedUser | null>(null);

   constructor() {
      this.loadFromStorage();
   }

   /** Charge l'utilisateur depuis le localStorage */
   private loadFromStorage() {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) {
         this.cachedUser = null;
         this.userSubject.next(this.cachedUser);
         return;
      }

      try {
         const parsed = JSON.parse(raw) as ConnectedUser;
         this.cachedUser = parsed ? {
            ...parsed,
            roles: this.normalizeRoles(parsed.roles)
         } : null;
      } catch {
         this.cachedUser = null;
      }

      this.userSubject.next(this.cachedUser);
   }

   /** Renvoie l'utilisateur chargé (ou null) */
   getUser(): ConnectedUser | null {
      return this.cachedUser;
   }
   setUser(user: ConnectedUser | null): void {
      this.cachedUser = user ? {
         ...user,
         roles: this.normalizeRoles(user.roles)
      } : null;
      if (user) {
         localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.cachedUser));
      } else {
         localStorage.removeItem(this.STORAGE_KEY);
      }
      this.userSubject.next(this.cachedUser);
   }
   reload(): void {
      this.loadFromStorage();
   }

   changes(): Observable<ConnectedUser | null> {
      return this.userSubject.asObservable();
   }

   /** Renvoie une propriété avec fallback */
   get<K extends keyof ConnectedUser>(key: K): ConnectedUser[K] | null {
      return this.cachedUser?.[key] ?? null;
   }

   /** Expose directement quelques helpers */
   getFirstname(): string | null {
      return this.get('firstname') ?? null;
   }

   getLastname(): string | null {
      return this.get('lastname') ?? null;
   }

   getPrimaryAccountNumber(): string | null {
      const n = this.get('primaryAccountNumber');
      return n ? String(n) : null;
   }

   getPrimaryAccountBalance(): string | null {
      const s = this.get('primaryAccountBalance');
      return s !== null ? String(s) : null;
   }

   getSigle(): string | null {
      return this.get('primaryAccountSigle');
   }
   getDisplayPrimaryAccountNumber(fallback?: string): string {
      const num = this.cachedUser?.primaryAccountNumber ?? fallback ?? '';
      return num.toString();
   }

   getDisplayPrimaryAccountBalance(fallback?: number | string): number | string {
      return this.cachedUser?.primaryAccountBalance ?? fallback ?? 0;
   }

   getLandingRoute(roles: string[]): string {
      if (!Array.isArray(roles)) {
         return '/connection';
      }
      const normalizedRoles = this.normalizeRoles(roles);

      // priorité des rôles
      if (normalizedRoles.includes('SUPER_ADMIN') || normalizedRoles.includes('ADMIN')) {
         return '/utilisateur/admin/dashboard';
      }

      if (normalizedRoles.includes('BANK')) {
         return '/utilisateur/admin/bank/deposits';
      }

      if (normalizedRoles.includes('GESTIONNAIRE')) {
         return '/utilisateur/gestionnaire/espace';
      }

      if (normalizedRoles.includes('CAISSIER')) {
         return '/utilisateur/bspace';
      }

      if (
         normalizedRoles.includes('MICROFINANCE') ||
         normalizedRoles.includes('MICROFINANCE_SUPERVISOR') ||
         normalizedRoles.includes('MICROFINANCE_CAISSE') ||
         normalizedRoles.includes('MICROFINANCE_COLLECTE') ||
         normalizedRoles.includes('MICROFINANCE_CREDIT')
      ) {
         return '/utilisateur/partenaire/dashboard';
      }

      if (normalizedRoles.includes('PLATFORM_ADMIN') || normalizedRoles.includes('SPAY_OPERATOR')) {
         return '/utilisateur/partner/dashboard';
      }

      // rôle par défaut
      if (normalizedRoles.includes('USER')) {
         return '/utilisateur/espace-client';
      }

      // fallback sécurité
      return '/connection';
   }

   private normalizeRoles(roles: string[] | null | undefined): string[] {
      if (!Array.isArray(roles)) {
         return [];
      }
      return Array.from(new Set(
         roles
            .map((role) => this.normalizeRole(role))
            .filter((role) => role.length > 0)
      ));
   }

   private normalizeRole(role: string | null | undefined): string {
      if (!role) {
         return '';
      }
      return role.toUpperCase().replace(/^ROLE_/, '').trim();
   }


}
